/**
 * LayoutPresetSelector — Dashboard view mode switcher
 *
 * Presets:
 *   operations  — KPIs + leads table + analytics collapsed
 *   analytics   — KPIs + analytics expanded + table minimized
 *   executive   — KPIs + compact summary only
 *
 * Keyboard shortcuts (desktop only, disabled in inputs/modals):
 *   1 / Ctrl+Shift+O → Operations
 *   2 / Ctrl+Shift+A → Analytics
 *   3 / Ctrl+Shift+E → Executive
 *
 * Persists to localStorage: treatx_dashboard_layout_preset
 * Mobile: only operations + executive, no keyboard shortcuts
 */
import React, { useState, useEffect, useCallback } from 'react';
import { LayoutGrid, BarChart3, Briefcase, Keyboard } from 'lucide-react';

const STORAGE_KEY = 'treatx_dashboard_layout_preset';

const PRESETS = [
  { key: 'operations', label: 'Operations', icon: LayoutGrid, mobileVisible: true, shortcut: '1' },
  { key: 'analytics', label: 'Analytics', icon: BarChart3, mobileVisible: false, shortcut: '2' },
  { key: 'executive', label: 'Executive', icon: Briefcase, mobileVisible: true, shortcut: '3' },
];

const SHORTCUT_MAP = { '1': 'operations', '2': 'analytics', '3': 'executive' };
const COMBO_MAP = { o: 'operations', a: 'analytics', e: 'executive' };

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768;

const isInputFocused = () => {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  return false;
};

const isModalOpen = () => !!document.querySelector('[data-testid="confirm-modal"], [role="dialog"], [data-radix-portal]');

const getInitial = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && PRESETS.some(p => p.key === stored)) return stored;
  } catch {}
  return 'operations';
};

export const LayoutPresetSelector = ({ value, onChange }) => {
  const [mobile, setMobile] = useState(isMobile);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    const handler = () => setMobile(isMobile());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const visible = PRESETS.filter(p => !mobile || p.mobileVisible);

  return (
    <div className="flex items-center gap-1.5" data-testid="layout-preset-selector">
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
        {visible.map(p => {
          const active = value === p.key;
          return (
            <button
              key={p.key}
              onClick={() => onChange(p.key)}
              title={!mobile ? `${p.label} (${p.shortcut})` : p.label}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all duration-200 ${
                active
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`preset-${p.key}`}
            >
              <p.icon className="h-3 w-3" />
              <span className="hidden sm:inline">{p.label}</span>
              {!mobile && <span className="hidden lg:inline text-[9px] text-slate-400 ml-0.5">{p.shortcut}</span>}
            </button>
          );
        })}
      </div>
      {/* Keyboard tip — desktop only */}
      {!mobile && (
        <div className="relative" data-testid="shortcut-tip-wrapper">
          <button
            onMouseEnter={() => setShowTip(true)}
            onMouseLeave={() => setShowTip(false)}
            className="p-1 text-slate-300 hover:text-slate-500 transition-colors"
            data-testid="shortcut-tip-trigger"
            aria-label="Keyboard shortcuts"
          >
            <Keyboard className="h-3.5 w-3.5" />
          </button>
          {showTip && (
            <div
              className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 bg-slate-800 text-white text-[10px] rounded-lg px-3 py-2 whitespace-nowrap shadow-lg z-40"
              data-testid="shortcut-tooltip"
            >
              <div className="font-semibold mb-1">Keyboard Shortcuts</div>
              <div className="space-y-0.5 text-slate-300">
                <div><kbd className="px-1 bg-slate-700 rounded text-[9px]">1</kbd> Operations</div>
                <div><kbd className="px-1 bg-slate-700 rounded text-[9px]">2</kbd> Analytics</div>
                <div><kbd className="px-1 bg-slate-700 rounded text-[9px]">3</kbd> Executive</div>
              </div>
              <div className="border-t border-slate-700 mt-1.5 pt-1 text-slate-400">
                Also: Ctrl+Shift+O / A / E
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const useLayoutPreset = () => {
  const [preset, setPreset] = useState(getInitial);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, preset); } catch {}
  }, [preset]);

  // On mobile, if analytics is selected, fall back to operations
  useEffect(() => {
    if (isMobile() && preset === 'analytics') {
      setPreset('operations');
    }
  }, [preset]);

  // Keyboard shortcuts — desktop only
  const handleKeyDown = useCallback((e) => {
    if (isMobile()) return;
    if (isInputFocused()) return;
    if (isModalOpen()) return;

    // Number keys: 1, 2, 3
    if (!e.ctrlKey && !e.metaKey && !e.altKey && SHORTCUT_MAP[e.key]) {
      e.preventDefault();
      setPreset(SHORTCUT_MAP[e.key]);
      return;
    }

    // Ctrl+Shift combos
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && COMBO_MAP[e.key.toLowerCase()]) {
      e.preventDefault();
      setPreset(COMBO_MAP[e.key.toLowerCase()]);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return [preset, setPreset];
};

/**
 * Visibility rules per preset:
 *   analytics:  { analyticsExpanded: true,  showTable: false, showSourceCards: false, showQuickActions: false }
 *   operations: { analyticsExpanded: false, showTable: true,  showSourceCards: true,  showQuickActions: true  }
 *   executive:  { analyticsExpanded: false, showTable: false, showSourceCards: false, showQuickActions: false }
 */
export const getPresetVisibility = (preset) => ({
  analyticsExpanded: preset === 'analytics',
  analyticsVisible: preset !== 'executive',
  showTable: preset === 'operations',
  showSourceCards: preset === 'operations',
  showQuickActions: preset === 'operations',
});

export default LayoutPresetSelector;
